import {
	MACPObservation,
	MSCCObservation,
	Observation,
	PACPObservation,
	VideoObservation,
	WSA05Observation,
} from '@Types/observation.types';
import { InspectionStandard } from '@Types/inspection.types';

export const getStartCodeForStandard = (
	standard: InspectionStandard,
): string | null => {
	switch (standard) {
		case InspectionStandard.WSA05:
			return 'TC';
		case InspectionStandard.MSCC:
			return 'VVR';
		default:
			return null;
	}
};

export const getObservationCode = (
	obs: Observation,
	standard: InspectionStandard,
): string => {
	switch (standard) {
		case InspectionStandard.PACP:
			return (obs as PACPObservation).PACPCode ?? '';
		case InspectionStandard.MACP:
			return (obs as MACPObservation).macpCode ?? '';
		case InspectionStandard.MSCC:
		case InspectionStandard.WSA05:
			return (
				(obs as MSCCObservation).code ??
				(obs as WSA05Observation).code ??
				''
			);
		default:
			return '';
	}
};

const parseTimeToSeconds = (time: string): number | null => {
	const parts = time.split(':');
	if (parts.length < 3) return null;
	const h = Number.parseInt(parts[0], 10);
	const m = Number.parseInt(parts[1], 10);
	const s = Number.parseFloat(parts[2]);
	if (Number.isNaN(h) || Number.isNaN(m) || Number.isNaN(s)) return null;
	return h * 3600 + m * 60 + s;
};

const getObservationTimeSeconds = (
	obs: Observation,
	standard: InspectionStandard,
): number | null => {
	let timeStr: string | undefined;
	switch (standard) {
		case InspectionStandard.WSA05:
			timeStr = (obs as WSA05Observation).videoPosition;
			break;
		case InspectionStandard.MSCC:
			timeStr = (obs as MSCCObservation).videoRef;
			break;
		default:
			return null;
	}
	if (!timeStr || timeStr.length < 6) return null;
	return parseTimeToSeconds(timeStr);
};

const getTabIndexFromSegmentStarts = (
	segmentStarts: number[],
	targetIdx: number,
	videoCount: number,
): number => {
	if (segmentStarts.length < 2) return 0;

	let tabIndex = 0;
	for (let i = 0; i < segmentStarts.length; i++) {
		if (segmentStarts[i] <= targetIdx) {
			tabIndex = i;
		} else {
			break;
		}
	}
	return Math.min(tabIndex, videoCount - 1);
};

const buildStartCodeSegments = (
	observations: Observation[],
	standard: InspectionStandard,
	startCode: string,
): number[] => {
	const startSet = new Set<number>([0]);
	for (let i = 1; i < observations.length; i++) {
		if (getObservationCode(observations[i], standard) === startCode) {
			startSet.add(i);
		}
		const prevT = getObservationTimeSeconds(observations[i - 1], standard);
		const currT = getObservationTimeSeconds(observations[i], standard);
		if (prevT !== null && currT !== null && prevT > 0 && currT < 10) {
			startSet.add(i);
		}
	}
	return Array.from(startSet).sort((a, b) => a - b);
};

const buildCounterResetSegments = (observations: Observation[]): number[] => {
	const segmentStarts = [0];
	for (let i = 1; i < observations.length; i++) {
		const prev = (observations[i - 1] as VideoObservation).counter;
		const curr = (observations[i] as VideoObservation).counter;
		if (prev !== undefined && curr !== undefined && curr < prev) {
			segmentStarts.push(i);
		}
	}
	return segmentStarts;
};

export const getVideoTabIndexForObservation = (
	observations: Observation[],
	target: Observation,
	standard: InspectionStandard,
	videoCount: number,
): number => {
	if (videoCount < 2) return 0;

	const targetIdx = observations.findIndex(
		o => String(o.conditionId) === String(target.conditionId),
	);
	if (targetIdx === -1) return 0;

	const startCode = getStartCodeForStandard(standard);
	const segmentStarts = startCode
		? buildStartCodeSegments(observations, standard, startCode)
		: buildCounterResetSegments(observations);

	return getTabIndexFromSegmentStarts(segmentStarts, targetIdx, videoCount);
};
